import React, { useState } from "react";
import { Doughnut } from "react-chartjs-2";
import { FormattedMessage, useIntl } from "react-intl";
import ChartDataLabels from "chartjs-plugin-datalabels";
import { Space, Row } from "@masterteam/mt-design-react";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";

import useIsAr from "../../Hooks/useIsAr";
import DetailsModal from "./DetailsModal";

import "./styles.scss";

const initialModalState = {
  isOpen: false,
  data: null,
};

ChartJS.register(ArcElement, Tooltip, Legend, ChartDataLabels);

const DoughnutChart = ({
  data,
  statuses,
  count,
  title,
  detailsTitle,
  maxWidth = "200px",
  textSize = "24px",
  showNoData = false,
  hideTooltip = false,
  smallTooltip = false,
  detailsData,
  isProject = false,
  detailsGroupedByObjectives = false,
  isInitiative = false,
  noClickEvent=false,
  levels,
  staticPlannedColor
}) => {
  const [isAr, locale] = useIsAr();
  const intl = useIntl();
  const [detailsModal, setDetailsModal] = useState(initialModalState);

  if (!data?.length && showNoData)
    return (
      <Row
        justify="center"
        align="center"
        style={{ height: "100%", padding: "16px 0", fontSize: "14px" }}
      >
        <FormattedMessage id="no-data" />
      </Row>
    );

  const options = {
    plugins: {
      locale,
      legend: false,
      filler: {
        propagate: true,
      },
      datalabels: {
        display: false,
        color: "#fff",
        align: isAr ? "right" : "center",
        padding: {
          right: -3,
        },
      },
      tooltip: {
        enabled: !hideTooltip,
        rtl: isAr,
        textDirection: isAr ? "rtl" : "ltr",
        xAlign: isAr ? "right" : "left",
        usePointStyle: true,
        displayColors: false,
        callbacks: {
          label: (e) => {
            const item =
              statuses.find(
                (el) =>
                  e.label === el?.Arabic ||
                  e.label === el?.ArabicName ||
                  e.label === el?.English ||
                  e.label === el?.EnglishName
              ) || null;

            const element =
              data?.find(
                (el) => el.Status === item.Code || el.Status === item.Value
              ) || null;

            if (item && element)
              return [
                `${intl.formatMessage({ id: "common.number" })}: ${
                  element?.Count
                }       `,
                `${intl.formatMessage({ id: "common.percentage" })}: ${
                  element?.Percentage
                }%`,
              ];
            else return null;
          },
        },
        bodySpacing: smallTooltip ? 2 : 4,
        boxPadding: 4,
        borderWidth: 1,
        cornerRadius: 5,
        zIndex: 999,
        backgroundColor: "#FFFFFF",
        bodyColor: "black",
        borderColor: "#707070",
        bodyFont: {
          family: "Cairo",
          size: smallTooltip ? 10 : 13,
        },
      },
    },
    cutout: "65%",
    responsive: true,
    maintainAspectRatio: true,
  };

  const _data = {
    labels: statuses?.map((item) =>
      isAr ? item.ArabicName || item.Arabic : item.EnglishName || item.English
    ),
    datasets: [
      {
        backgroundColor: statuses?.map((item) => item.Color),
        data: statuses?.map(
          (item) =>
            data?.find((e) => e.Status === item.Code || e.Status === item.Value)
              ?.Count || ""
        ),
      },
    ],
  };

  return (
    <div
      className="chart"
      style={{
        position: "relative",
        width: "100%",
        maxWidth: maxWidth,
      }}
    >
      <Doughnut
        data={_data}
        options={options}
        onClick={noClickEvent ? () => null : (e) => {
          e.stopPropagation();
          if (data.length) setDetailsModal({ isOpen: true, data: detailsData });
        }}
      />
      <Space
        align="center"
        direction="vertical"
        className="total"
        style={{ fontSize: textSize }}
      >
        <strong>{count}</strong>
        {title ? <p>{title}</p> : null}
      </Space>

      {detailsModal.isOpen && (
        <DetailsModal
          statuses={statuses || []}
          data={detailsModal.data}
          isOpen={detailsModal.isOpen}
          title={detailsTitle}
          onClose={() => setDetailsModal(initialModalState)}
          isProject={isProject}
          detailsGroupedByObjectives={detailsGroupedByObjectives}
          isInitiative={isInitiative}
          levels={levels}
          staticPlannedColor={staticPlannedColor}
        />
      )}
    </div>
  );
};

export default DoughnutChart;
